import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, TextField, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import B2BCatalogApi, { ICatalogGroupBackendData } from 'api/b2b/CatalogApiB2b'
import OrbitSession from 'hooks/useCookies'

import CatalogList from './CatalogList'

function AddCatalogGroup() {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: { catalogGroupName: '', search: '' }
  })
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const faberToken = OrbitSession.get('faberToken')
  const userEmail = OrbitSession.get('email') || ''
  const mode = searchParams.get('mode')
  const catalogGroupId = searchParams.get('id') || ''
  const shouldFetchCatalogGroup = Boolean(mode && catalogGroupId)
  const [catalogItems, setCatalogItems] = useState<string[]>([])

  // This will only work if the mode is edit and id is present
  const { data: catalogGroupData } = useQuery(
    ['catalogGroupData', mode, catalogGroupId],
    async () => {
      const response = await B2BCatalogApi.getCatalogGroupById(
        faberToken,
        catalogGroupId
      )
      return response.data
    },
    {
      enabled: shouldFetchCatalogGroup,
      refetchOnMount: 'always',
      onError: () => {
        toast.error('Error fetching catalog group details')
      }
    }
  )
  // On creating the group
  const { mutate: createCatalogGroup, isLoading: isCreatingCatalogGroup } =
    useMutation(
      (data: ICatalogGroupBackendData) =>
        B2BCatalogApi.createCatalogGroup(data, faberToken),
      {
        onSuccess: () => {
          toast.success('Catalog created successfully')
          // Invalidate the cache for search results
          queryClient.invalidateQueries({
            queryKey: ['catalogData'],
            refetchType: 'none'
          })
          navigate('/hq/b2b/catalog', { state: { reset: true } })
        },
        onError: (error) => {
          console.error('API Error:', error)
          toast.error('Failed to create catalog')
        }
      }
    )
  // On updating the group
  const { mutate: updateCatalogGroup, isLoading: isUpdatingCatalogGroup } =
    useMutation(
      (data: ICatalogGroupBackendData) =>
        B2BCatalogApi.updateCatalogGroup(data, faberToken),
      {
        onSuccess: () => {
          toast.success('Catalog updated successfully')
          // Invalidate the cache for search results
          queryClient.invalidateQueries({
            queryKey: ['catalogGroupData',mode, catalogGroupId],
            refetchType: 'none'
          })
          navigate('/hq/b2b/catalog', { state: { reset: true } })
        },
        onError: (error) => {
          console.error('API Error:', error)
          toast.error('Failed to update catalog')
        }
      }
    )

  const onSubmit = (formData) => {
    const catalogGroupName = formData.catalogGroupName

    if (!catalogGroupName.trim()) {
      toast.error('Catalog Group Name is required')
      return
    }

    const catalogGroupPayload = catalogItems.map((id) => ({
      catalog_destination_id: id,
      is_active: true

    }))

    const payload: ICatalogGroupBackendData = {
      name: catalogGroupName,
      is_active: true,
      created_by: userEmail, 
      catalog_group_item: catalogGroupPayload
    }
    if (mode === 'edit') {
      payload.updated_by = userEmail
      payload.id = catalogGroupId
      updateCatalogGroup(payload)
    } else createCatalogGroup(payload)
  }

  useEffect(() => {
    if (catalogGroupData) {
      reset({
        catalogGroupName: catalogGroupData.name || ''
      })

      setCatalogItems(() =>
        catalogGroupData.catalog_group_item.map((item) =>
          String(item.catalog_destination_id)
        )
      )
    }
  }, [catalogGroupData, reset])

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px', fontSize: '14px' }}>
            Catalog Group Name*
          </Typography>
          <Controller
            name='catalogGroupName'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field }) => (
              <TextField
                id='catalogGroupName'
                variant='outlined'
                size='small'
                error={!!errors.catalogGroupName}
                helperText={errors.catalogGroupName?.message}
                {...field}
              />
            )}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <CatalogList
            setCatalogItems={setCatalogItems}
            catalogItems={catalogItems}
          />
        </Box>

        <Box sx={{ mt: '20px' }}>
          <Button
            type='submit'
            variant='contained'
            disableElevation
            disabled={isCreatingCatalogGroup}
          >
            {isCreatingCatalogGroup || isUpdatingCatalogGroup ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default AddCatalogGroup
